import React, { useRef, useState } from 'react'
import { FormProvider, SubmitHandler, useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { Stack } from '@mui/material'
import { useQueryClient } from '@tanstack/react-query'
import { useParams } from 'react-router-dom'
import { getDynamicTariffSettingsQueryKey, useFetchDynamicTariffSettings, useValidateDynamicTariffSettingsForm } from '../../hooks'
import SettingsFormContainer from '../../../form/components/SettingsFormContainer'
import { GridFeeState, PowerPriceState } from '../../dto'
import { createDynamicTariffSettings } from '../../api'
import { useAuthFetchWithResponse } from '@e3dc-react/shell/Hooks/AuthFetch'
import { useSnackbar } from '@e3dc-react/shell/Hooks/Snackbar'
import { useCustomTranslation } from '@e3dc-react/shell/Hooks/useCustomTranslation'
import { GridFeesSettingsForm } from './GridFeesSettingsForm'
import { PowerPricesSettingsForm } from './PowerPricesForm'
import { DynamicTariffSettingsModel } from '../../model'
import { dynamicTariffSettingsModelToDtoTransformer } from '../../transformer'
import { DynamicPricesSettings } from 'src/Libs/dynamic-power-prices/types'
import { useDynamicEms, useIsDynamicEmsAccessible, useUpdateDynamicEmsSettings } from 'src/Libs/dynamic-power-prices'
import { ConfirmationDialog } from '@e3dc-react/shell/Elements/Dialog'
import { Typography } from '@e3dc-react/shell/Elements/Typography'
import { useFeatureFlagsContext } from 'src/Libs/feature-flags/FeatureFlagsContext'
import { OptionalTooltipWrapper } from '@e3dc-react/shell/Elements/Tooltip'

const DEFAULT_VALUES: DynamicTariffSettingsModel = {
  gridFeeState: GridFeeState.Included,
  gridFees: null,
  powerPriceState: PowerPriceState.Static,
  powerPrices: 0,
  vatPercent: 0,
}

interface Props {
  hasWritingRights: boolean
  dynamicPricesSettings: DynamicPricesSettings | null
  handleDynamicPricesSettingsUpdate: (newValues: DynamicPricesSettings) => Promise<void>
}

/**
 * Loads the dynamic tariff settings and mounts the form only once they are available,
 * so the form is initialised with the correct values and does not flicker from the defaults.
 */
const DynamicTariffSettingsFormProvider: React.FC<Props> = props => {
  const { installationId } = useParams<{ installationId: string }>()
  const { dynamicTariffSettings, loading } = useFetchDynamicTariffSettings(installationId)

  if (loading) return null
  return <DynamicTariffSettingsForm {...props} initialValues={dynamicTariffSettings ?? DEFAULT_VALUES} />
}

interface FormProps extends Props {
  initialValues: DynamicTariffSettingsModel
}

/**
 * Form to set and edit Dynamic tariff settings.
 * Mounted only once the settings are loaded so the form is seeded with the correct default values.
 */
const DynamicTariffSettingsForm: React.FC<FormProps> = ({
  hasWritingRights,
  dynamicPricesSettings,
  handleDynamicPricesSettingsUpdate,
  initialValues,
}) => {
  const { installationId } = useParams<{ installationId: string }>()
  const { t } = useCustomTranslation()
  const { enqueueSuccessStack, enqueueErrorStack } = useSnackbar()
  const { dynamicTariffSettingsFormSchema } = useValidateDynamicTariffSettingsForm()
  const { dynamicTariffSettings } = useFetchDynamicTariffSettings(installationId)
  const { data: dynamicEmsSettings } = useDynamicEms(installationId, !!installationId)
  const { mutateAsync: updateDynamicEmsSettings } = useUpdateDynamicEmsSettings()
  const { isDynamicEmsV2Accessible } = useIsDynamicEmsAccessible()
  const authFetch = useAuthFetchWithResponse()
  const queryClient = useQueryClient()
  const pendingValuesRef = useRef<DynamicTariffSettingsModel | null>(null)
  const [isConfirmDeactivationDialogOpen, setIsConfirmDeactivationDialogOpen] = useState(false)

  const {
    featureFlags: { isTariffSettingBoxesEnabled },
    isFeatureFlagsLoading,
  } = useFeatureFlagsContext()
  const isTariffSettingBoxesEnabledWithRights = !isFeatureFlagsLoading && isTariffSettingBoxesEnabled && hasWritingRights

  const methods = useForm({
    resolver: zodResolver(dynamicTariffSettingsFormSchema),
    defaultValues: initialValues as never,
    values: (dynamicTariffSettings ?? initialValues) as never,
    mode: 'onSubmit',
    disabled: !isTariffSettingBoxesEnabledWithRights,
  })

  const { handleSubmit, reset } = methods

  const queryKey = getDynamicTariffSettingsQueryKey(installationId)

  const onReset = (): void => {
    reset()
  }

  const createTariffSettings = async (data: DynamicTariffSettingsModel): Promise<void> => {
    const payload = dynamicTariffSettingsModelToDtoTransformer(data)
    const { hasErrors } = await createDynamicTariffSettings(installationId, payload, authFetch)

    if (hasErrors) {
      enqueueErrorStack(t('dynamic-tariff-settings.failure-message'))
      return
    }

    queryClient.invalidateQueries({ queryKey })
    enqueueSuccessStack(t('dynamic-tariff-settings.success-message'))
  }

  const onSubmit: SubmitHandler<DynamicTariffSettingsModel> = async (data): Promise<void> => {
    const isSwitchingToStaticPowerPrices = data.powerPriceState === PowerPriceState.Static
    const isGreedFeeStatic = data.gridFeeState === GridFeeState.Static || data.gridFeeState === GridFeeState.Included
    if (isSwitchingToStaticPowerPrices && isGreedFeeStatic && dynamicPricesSettings) {
      /**
       * Case 1
       * feedInTariffType is set
       * isDynamicEmsEnabledForBattery is enabled
       * Expected result: AI360 2.0 can be active but battery optimization has to be deactivated, {isDynamicEmsEnabledForBattery: true, dynamicPricesForBatteryEnabled: true}
       */
      if (dynamicEmsSettings?.feedInTariffType && dynamicEmsSettings?.isDynamicEmsEnabledForBattery) {
        pendingValuesRef.current = data
        setIsConfirmDeactivationDialogOpen(true)
        return
      }

      /**
       * Case 2
       * feedInTariffType is not set
       * isDynamicEmsEnabledForBattery is enabled
       * Expected result: AI360 2.0 has to be deactivated, {isDynamicEmsEnabledForBattery: false, dynamicPricesForWallboxesEnabled: false, dynamicPricesForBatteryEnabled: false}
       */
      if (!dynamicEmsSettings?.feedInTariffType && dynamicEmsSettings?.isDynamicEmsEnabledForBattery) {
        pendingValuesRef.current = data
        setIsConfirmDeactivationDialogOpen(true)
        return
      }
    }

    /* Without AI360 2.0 the price based charging is deactivated when switching from dynamic to static prices */
    const isAi360V2Active = isDynamicEmsV2Accessible && !!dynamicEmsSettings?.isActive
    const isPowerPriceDynamic =
      dynamicTariffSettings?.powerPriceState === PowerPriceState.Dynamic || dynamicTariffSettings?.powerPriceState === PowerPriceState.Manual
    const isPriceBasedChargingEnabled =
      !!dynamicPricesSettings?.dynamicPricesForWallboxesEnabled ||
      !!dynamicPricesSettings?.dynamicPricesForBatteryEnabled ||
      !!dynamicPricesSettings?.isDynamicEmsEnabledForBattery

    if (!isAi360V2Active && isPowerPriceDynamic && isSwitchingToStaticPowerPrices && isPriceBasedChargingEnabled) {
      pendingValuesRef.current = data
      setIsConfirmDeactivationDialogOpen(true)
      return
    }

    await createTariffSettings(data)
  }

  const handleConfirmDeactivationDialog = async (): Promise<void> => {
    setIsConfirmDeactivationDialogOpen(false)
    if (pendingValuesRef.current && dynamicPricesSettings) {
      if (dynamicEmsSettings?.feedInTariffType) {
        const { isActive, ...emsSettings } = dynamicEmsSettings
        await updateDynamicEmsSettings({
          installationId: installationId!,
          ...emsSettings,
          isDynamicEmsEnabledForBattery: false,
          isSgReadyEnabled: false,
        })
      } else {
        await handleDynamicPricesSettingsUpdate({
          ...dynamicPricesSettings,
          isDynamicEmsEnabledForBattery: false,
          dynamicPricesForWallboxesEnabled: false,
          dynamicPricesForBatteryEnabled: false,
        })
      }

      await createTariffSettings(pendingValuesRef.current)
      pendingValuesRef.current = null
    }
  }

  return (
    <FormProvider {...methods}>
      <OptionalTooltipWrapper
        titleKey={isTariffSettingBoxesEnabledWithRights ? undefined : t('dynamic-tariff-settings.tariff-setting-boxes-disabled-tooltip')}
        placement="top"
      >
        <Stack component="form" onSubmit={handleSubmit(onSubmit)}>
          <SettingsFormContainer onReset={onReset}>
            <PowerPricesSettingsForm hasWritingRights={isTariffSettingBoxesEnabledWithRights} />
            <GridFeesSettingsForm hasWritingRights={isTariffSettingBoxesEnabledWithRights} />
          </SettingsFormContainer>
        </Stack>
      </OptionalTooltipWrapper>
      {isConfirmDeactivationDialogOpen && (
        <ConfirmationDialog
          open={isConfirmDeactivationDialogOpen}
          onClose={() => setIsConfirmDeactivationDialogOpen(false)}
          onConfirm={handleConfirmDeactivationDialog}
          title={t('dynamic-tariff-settings.disable-price-based-charging.title')}
          buttonText={[t('common.cancel'), t('common.confirm')]}
        >
          <Typography variant="body2">{t('dynamic-tariff-settings.disable-price-based-charging.description')}</Typography>
        </ConfirmationDialog>
      )}
    </FormProvider>
  )
}
export default DynamicTariffSettingsFormProvider
